/**
 * <SelectableList>
 *
 * Handles logic for maintaining selection status with checkboxes, providing a
 * context menu for each item in the list, and performing bulk actions on
 * selected items.
 *
 * Takes a lot of cues from Adobe's design library, ListView in particular:
 *
 * https://react-spectrum.adobe.com/react-spectrum/ListView.html
 *
 * Where possible, we should just mimic their Collection/Selection interface,
 * as it'll make the shift/cmd+click behavior easier to implement
 */

import React, { ReactNode } from 'react'

import { ActionIcon, Checkbox, Group, Menu, ScrollArea } from '@mantine/core'
import { Item } from '@react-stately/collections'
import { useListState } from '@react-stately/list'

import { ExplorerView } from '#components/ExplorerView'
import { FloatingMenuProvider, useFloatingMenu } from '#hooks'

export type Key = string
export type Selection = 'all' | Set<Key>
export type SelectionMode = 'none' | 'multiple'

export interface SelectableListItem {
  id: Key
  [key: string]: unknown
}

export interface SelectableListProps<T extends SelectableListItem> {
  items: T[]
  selectionMode?: SelectionMode
  onAction?(key: Key): void
  title: ReactNode
  headerActions?: ReactNode
  // Render prop pattern a la react-aria
  children: (item: T) => ReactNode
  // Render functions for menus
  renderContextMenu?: (item: T) => ReactNode
  renderBulkActionsMenu?: (selectedItems: T[], allItems: T[]) => ReactNode
}

function SelectableListInner<T extends SelectableListItem>(
  props: SelectableListProps<T>,
) {
  const {
    items,
    selectionMode = 'none',
    onAction,
    children,
    title,
    headerActions,
    renderContextMenu,
    renderBulkActionsMenu,
  } = props
  const floatingMenu = useFloatingMenu()
  const [contextMenuItem, setContextMenuItem] = React.useState<T | null>(null)
  const listState = useListState<T>({
    selectionMode,
    children: items.map((item) => (
      <Item key={item.id} textValue={item.id}>
        {item.id}
      </Item>
    )),
  })
  const selectionManager = listState.selectionManager

  const showCheckboxes = selectionMode === 'multiple'

  // Mantine's checkbox group represents checked values as an array. Normalize
  // React Stately's selection shape for that interface.
  const selectedIds = showCheckboxes
    ? Array.from(selectionManager.selectedKeys, String)
    : []

  const selectedItems = items.filter((item) => selectedIds.includes(item.id))
  const allSelected =
    selectionManager.isSelectAll ||
    (selectionManager.selectedKeys.size === items.length && items.length > 0)
  const someSelected =
    selectionManager.selectedKeys.size > 0 &&
    selectionManager.selectedKeys.size < items.length

  const handleSelectAll = () => {
    selectionManager.toggleSelectAll()
  }

  const handleItemClick = (item: T) => {
    onAction?.(item.id)
  }

  const handleContextMenu = (item: T, event: React.MouseEvent) => {
    setContextMenuItem(item)
    floatingMenu.handleContextMenu(event)
  }

  const headerTitle =
    showCheckboxes && items.length > 0 ? (
      <Group gap="xs">
        <Checkbox
          aria-label="Select all items"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleSelectAll}
          size="sm"
          ms={2}
        />
        {title}
      </Group>
    ) : (
      title
    )

  const headerActionsWithBulk = renderBulkActionsMenu ? (
    <Group gap="xs">
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionIcon color="dark" px="lg" py="sm">
            {headerActions}
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {renderBulkActionsMenu(selectedItems, items)}
        </Menu.Dropdown>
      </Menu>
    </Group>
  ) : (
    headerActions
  )

  const itemElements = items.map((item) => {
    return (
      <div
        key={item.id}
        onContextMenu={(event) => handleContextMenu(item, event)}
        onClick={(event) => {
          // Don't trigger onAction if clicking on a checkbox
          if (
            event.target instanceof HTMLInputElement &&
            event.target.type === 'checkbox'
          ) {
            return
          }
          handleItemClick(item)
        }}
        style={{ position: 'relative', cursor: 'pointer' }}
      >
        {children(item)}
      </div>
    )
  })

  return (
    <>
      <ExplorerView.Header
        title={headerTitle}
        buttons={headerActionsWithBulk}
      />
      <ScrollArea h="100%">
        {showCheckboxes ? (
          <Checkbox.Group
            value={selectedIds}
            onChange={(values) => {
              selectionManager.setSelectedKeys(values)
            }}
            onClick={(event) => {
              if (
                event.shiftKey &&
                event.target instanceof HTMLInputElement &&
                event.target.type === 'checkbox'
              ) {
                // TODO: Handle shift+click range selection
              }
            }}
          >
            {itemElements}
          </Checkbox.Group>
        ) : (
          itemElements
        )}
      </ScrollArea>
      {contextMenuItem && renderContextMenu && (
        <Menu
          opened={floatingMenu.opened}
          onClose={floatingMenu.hide}
          withinPortal
        >
          <Menu.Target>
            <div style={floatingMenu.targetStyle} />
          </Menu.Target>
          <Menu.Dropdown>{renderContextMenu(contextMenuItem)}</Menu.Dropdown>
        </Menu>
      )}
    </>
  )
}

export function SelectableList<T extends SelectableListItem>(
  props: SelectableListProps<T>,
) {
  return (
    <FloatingMenuProvider>
      <SelectableListInner key={props.selectionMode ?? 'none'} {...props} />
    </FloatingMenuProvider>
  )
}
